import { z } from 'zod';
import { CUSTOMER_RBAC_ROLES } from './rbac-roles.ts';
import type { CognitoUserStatus } from './types/users.ts';
import {
  pagingFields,
  parseSearchParams,
  type SearchFieldKinds,
  searchId,
  searchText,
  toSearchParams,
} from './search-spec.ts';

/**
 * WaterScope Plus user search filter spec (Smart Search, MET-3530).
 *
 * Plus-pool users only; Wellspring staff are out of scope. Users live in
 * Cognito, not the database, so `GET /users/filter` gathers the same list as
 * `GET /users/with-customers` and applies this spec in memory.
 *
 * Emails and names are PII: they may appear in `text`, which is never logged.
 */

export const USER_SEARCH_SORT_COLUMNS = ['email', 'name', 'createdAt', 'lastLoginAt'] as const;

/** Assignable Plus roles plus the end-consumer group (all RBAC-managed groups). */
export const USER_SEARCH_ROLES = [
  ...Object.values(CUSTOMER_RBAC_ROLES),
  'end-consumer',
] as const;

export const USER_SEARCH_STATUSES = [
  'UNCONFIRMED',
  'CONFIRMED',
  'ARCHIVED',
  'COMPROMISED',
  'UNKNOWN',
  'RESET_REQUIRED',
  'FORCE_CHANGE_PASSWORD',
  'EXTERNAL_PROVIDER',
] as const satisfies readonly CognitoUserStatus[];

// Ten years: a product choice, like meter search's contact windows.
const days = z.number().int().positive().max(3_650);

export const userSearchSchema = z
  .object({
    customerId: searchId.optional().describe('Only users with access to this customer'),
    text: searchText.optional().describe('Free-text match against user email or name'),
    rbacRole: z.enum(USER_SEARCH_ROLES).optional().describe('Only users with this role'),
    enabled: z.boolean().optional().describe('Only enabled (true) or disabled (false) accounts'),
    userStatus: z.enum(USER_SEARCH_STATUSES).optional()
      .describe('Cognito account status, e.g. FORCE_CHANGE_PASSWORD for never-activated'),
    federated: z.boolean().optional()
      .describe('Only users who sign in through Entra SSO (true) or with a password (false)'),
    lastLoginOlderThanDays: days.optional().describe(
      'Users whose last login was more than this many days ago. ' +
        'Users who have never logged in also match.',
    ),
    lastLoginWithinDays: days.optional().describe('Users who logged in within this many days'),
    createdWithinDays: days.optional().describe('Users created within this many days'),
    ...pagingFields(USER_SEARCH_SORT_COLUMNS),
  })
  .strict()
  .refine(
    (s) =>
      s.lastLoginOlderThanDays === undefined || s.lastLoginWithinDays === undefined ||
      s.lastLoginOlderThanDays < s.lastLoginWithinDays,
    {
      message: 'lastLoginOlderThanDays must be less than lastLoginWithinDays',
      path: ['lastLoginOlderThanDays'],
    },
  );

export type UserSearch = z.infer<typeof userSearchSchema>;

/** One row of `GET /users/filter`. */
export interface UserSearchRow {
  /** Cognito username; the stable sort tiebreaker. Not shown to users. */
  username: string;
  email: string;
  name: string;
  enabled: boolean;
  userStatus: CognitoUserStatus;
  isFederated: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  rbacRole: string | null;
  customers: Array<{ id: number; name: string }>;
}

export interface UserSearchResponse {
  users: UserSearchRow[];
  total: number;
  page: number;
  pageSize: number;
}

const USER_SEARCH_FIELDS: SearchFieldKinds = {
  customerId: 'number',
  text: 'string',
  rbacRole: 'string',
  enabled: 'boolean',
  userStatus: 'string',
  federated: 'boolean',
  lastLoginOlderThanDays: 'number',
  lastLoginWithinDays: 'number',
  createdWithinDays: 'number',
  page: 'number',
  pageSize: 'number',
  sortBy: 'string',
  sortDir: 'string',
};

/** Serialize a user search to URL params (defaults omitted; see `toSearchParams`). */
export function toUserSearchParams(search: UserSearch): URLSearchParams {
  return toSearchParams(search, USER_SEARCH_FIELDS);
}

/** Parse URL params into a validated user search (see `parseSearchParams`). */
export function parseUserSearchParams(
  params: URLSearchParams,
): ReturnType<typeof userSearchSchema.safeParse> {
  return parseSearchParams(userSearchSchema, USER_SEARCH_FIELDS, params);
}
